import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Clock, Zap, Scale, Users, ArrowRight, Shield, CheckCircle2 } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';

const Pricing: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <div className="text-center mb-8 sm:mb-12">
            <CreditCard className="h-12 w-12 sm:h-16 sm:w-16 text-primary-600 mx-auto mb-3 sm:mb-4" />
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
              Pricing & Plans
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Built for organizations of all sizes. Get started in minutes and scale with your needs.
            </p>
          </div>

          <div className="mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
              Start Automating Your Document Work Today
            </h2>
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-primary-50 border border-primary-200 p-5 rounded-lg">
                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Save Time</h3>
                    <p className="text-gray-600 text-sm">
                      Reduce document review time by 80%, allowing you to focus on high-value work.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-primary-50 border border-primary-200 p-5 rounded-lg">
                <div className="flex items-start">
                  <Zap className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Instant Results</h3>
                    <p className="text-gray-600 text-sm">
                      Get answers in seconds, not hours. Speed up decision-making and client responses.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-primary-50 border border-primary-200 p-5 rounded-lg">
                <div className="flex items-start">
                  <Scale className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Accurate Citations</h3>
                    <p className="text-gray-600 text-sm">
                      Every answer includes precise citations with page numbers for verification.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-primary-50 border border-primary-200 p-5 rounded-lg">
                <div className="flex items-start">
                  <Users className="h-6 w-6 text-primary-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Team Collaboration</h3>
                    <p className="text-gray-600 text-sm">
                      Share research and documents with your team. Organize by project or category.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-6 sm:p-8 rounded-lg mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-primary-600 mr-2" />
                Need Maximum Privacy & Security?
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                For organizations handling highly sensitive documents or requiring complete data control, we offer
                private AI deployment options. Your AI runs on your infrastructure, ensuring complete
                confidentiality and data protection.
              </p>
              <Link
                to="/private-ai"
                className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center"
              >
                Learn More About Private AI Deployment <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            <div className="text-center border-t border-gray-200 pt-8">
              <ul className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 mb-6">
                <li className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                  No credit card required
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                  Setup in minutes
                </li>
                <li className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                  Cancel anytime
                </li>
              </ul>
              <Link
                to="/register"
                className="inline-block bg-primary-600 text-white px-10 py-4 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                Start Your Free Trial Today
              </Link>
              <p className="text-gray-600 mt-4 text-sm">
                Contact us for custom plans and enterprise pricing.
              </p>
            </div>
          </div>

          <div className="text-center pt-6 border-t border-gray-200">
            <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">
              Get in touch for evaluation access or questions →
            </Link>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Pricing;
